import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { CompressOutlined, DeleteOutlined, ExpandOutlined, PlusOutlined, CopyOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Space, Table, Dropdown, Input } from 'antd';
import { useState, useRef } from 'react';
import styles from './EditableTable.module.scss';
import { CGButton } from '../../../components/CGComponents';
export const EditableTable = ({ columns, name, addText = 'Thêm dòng', addBtnSize = 'middle', canAdd = true, canEdit = true, canDelete = true, readOnly = false, pagination = false, bordered = true, size = 'small', handleRowClick: externalHandleRowClick, onAdd, onRemoved, disableDelete, initialValue = {}, actions, actionWidth, seamlessMode = true, childrenKey, copyableColumns, dependenciesColumn, searchableColumns = [], ...props }) => {
    const [editingKey, setEditingKey] = useState(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const searchInput = useRef(null);
    const form = Form.useFormInstance();
    const isEditing = (index) => editingKey === index.toString();
    const handleRowClick = (_, index) => {
        if (externalHandleRowClick) {
            externalHandleRowClick(_, index);
        }
        else if (!readOnly && canEdit) {
            setEditingKey(index.toString());
        }
    };
    // ===== helpers for keys & tree =====
    // genKey used both in mapTree and when mutating rawData
    const genKey = (record, parentKey, idx) => {
        return record.id ?? record.key ?? `${parentKey}${idx}`;
    };
    const mapTree = (rows, parentKey = '') => {
        return rows.map((row, idx) => {
            const uniqueKey = genKey(row, parentKey, idx);
            const node = { ...row, key: uniqueKey };
            if (childrenKey && Array.isArray(row[childrenKey])) {
                const mappedChildren = mapTree(row[childrenKey], uniqueKey + '-');
                if (mappedChildren.length > 0) {
                    node.children = mappedChildren;
                }
                else {
                    delete node.children;
                }
            }
            else {
                delete node.children;
            }
            return node;
        });
    };
    const rawData = Form.useWatch(name) || [];
    // flatten a tree that already has `key` assigned (mapTree output)
    const flattenWithKeys = (rows) => {
        let res = [];
        for (const r of rows) {
            res.push(r);
            if (childrenKey && r[childrenKey]?.length > 0) {
                res = res.concat(flattenWithKeys(r[childrenKey]));
            }
        }
        return res;
    };
    // getAllKeys from a key'd tree
    const getAllKeys = (rows) => {
        let keys = [];
        rows.forEach((row) => {
            if (row.key)
                keys.push(row.key);
            if (row.children)
                keys = keys.concat(getAllKeys(row.children));
        });
        return keys;
    };
    // applyChange: mutate rawData (the form value). If keysToMatch provided, only apply to records whose generated key is in set.
    const applyChange = (cb, keysToMatch) => {
        const recursive = (records, parentKey = '') => records.map((r, idx) => {
            const uniqueKey = genKey(r, parentKey, idx);
            const shouldApply = !keysToMatch || keysToMatch.has(uniqueKey);
            const newR = shouldApply ? cb(r, idx, parentKey) : { ...r };
            if (childrenKey && r[childrenKey]?.length > 0) {
                newR[childrenKey] = recursive(r[childrenKey], uniqueKey + '-');
            }
            return newR;
        });
        const changed = recursive(rawData);
        form.setFieldValue(name, changed);
    };
    // build dataSource (mapTree on rawData)
    const buildDataSourceWithKeys = () => mapTree(rawData);
    // ===== COPY HANDLERS =====
    const handlePasteAll = (col, value, selectedRow) => {
        const dep = dependenciesColumn?.find((x) => x.includes(`:${col}`))?.split(':')?.[0];
        if (childrenKey) {
            applyChange((rec) => {
                if (dep && selectedRow[dep]?.id !== rec[dep]?.id)
                    return rec;
                return { ...rec, [col]: value };
            });
        }
        else {
            // For flat table, use Form.List operation to update values
            const currentData = form.getFieldValue(name) || [];
            currentData.forEach((_, index) => {
                const currentRecord = form.getFieldValue([name, index]);
                if (dep && selectedRow[dep]?.id !== currentRecord[dep]?.id)
                    return;
                form.setFieldValue([name, index, col], value);
            });
        }
    };
    const handlePasteToEmpty = (col, value, selectedRow) => {
        const dep = dependenciesColumn?.find((x) => x.includes(`:${col}`))?.split(':')?.[0];
        if (childrenKey) {
            applyChange((rec) => {
                if (dep && selectedRow[dep]?.id !== rec[dep]?.id)
                    return rec;
                if (rec[col] === undefined || rec[col] === null || rec[col] === '') {
                    return { ...rec, [col]: value };
                }
                return rec;
            });
        }
        else {
            // For flat table, use Form.List operation to update values
            const currentData = form.getFieldValue(name) || [];
            currentData.forEach((_, index) => {
                const currentRecord = form.getFieldValue([name, index]);
                if (dep && selectedRow[dep]?.id !== currentRecord[dep]?.id)
                    return;
                const currentValue = form.getFieldValue([name, index, col]);
                if (currentValue === undefined || currentValue === null || currentValue === '') {
                    form.setFieldValue([name, index, col], value);
                }
            });
        }
    };
    const handlePasteBelow = (col, value, selectedRow, selectedIndex) => {
        const dep = dependenciesColumn?.find((x) => x.includes(`:${col}`))?.split(':')?.[0];
        if (childrenKey) {
            // build key'd dataSource first (same keys that mapTree produced in table)
            const dataSource = buildDataSourceWithKeys();
            const flat = flattenWithKeys(dataSource);
            const selectedKey = selectedRow.key ?? selectedRow.id;
            const idx = flat.findIndex((r) => r.key === selectedKey);
            if (idx === -1)
                return;
            const keysToUpdate = new Set(flat.slice(idx + 1).map((r) => r.key));
            applyChange((rec) => {
                if (dep && selectedRow[dep]?.id !== rec[dep]?.id)
                    return rec;
                return { ...rec, [col]: value };
            }, keysToUpdate);
        }
        else {
            // For flat table, use the provided selectedIndex or find it
            let rowIndex = selectedIndex;
            if (rowIndex === undefined) {
                const currentData = form.getFieldValue(name) || [];
                rowIndex = currentData.findIndex((rec) => rec.id === selectedRow.id || rec.key === selectedRow.key);
            }
            if (rowIndex === -1)
                return;
            // Use Form.List operation to update values for rows below selected
            const currentData = form.getFieldValue(name) || [];
            currentData.forEach((_, index) => {
                if (rowIndex !== undefined && index <= rowIndex)
                    return;
                const currentRecord = form.getFieldValue([name, index]);
                if (dep && selectedRow[dep]?.id !== currentRecord[dep]?.id)
                    return;
                form.setFieldValue([name, index, col], value);
            });
        }
    };
    const contextItems = (col, value, record, index) => [
        {
            key: 'group-1',
            label: (_jsxs(_Fragment, { children: [_jsx(CopyOutlined, {}), " Copy v\u00E0o"] })),
            type: 'group',
            children: [
                { key: '1', onClick: () => handlePasteAll(col, value, record), label: 'Tất cả các dòng' },
                { key: '2', onClick: () => handlePasteToEmpty(col, value, record), label: 'Các dòng còn trống' },
                { key: '3', onClick: () => handlePasteBelow(col, value, record, index), label: 'Các dòng bên dưới' }
            ]
        }
    ];
    // ===== SEARCH FUNCTIONALITY =====
    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
    };
    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (_jsxs("div", { style: { padding: 8 }, onKeyDown: (e) => e.stopPropagation(), children: [_jsx(Input, { ref: searchInput, placeholder: `Nhập nội dung và nhấn Enter`, value: selectedKeys[0], onChange: (e) => setSelectedKeys(e.target.value ? [e.target.value] : []), onPressEnter: () => confirm(), style: { marginBottom: 8, display: 'block' } }), _jsxs(Space, { children: [_jsx(Button, { type: 'primary', onClick: () => handleSearch(selectedKeys, confirm, dataIndex), icon: _jsx(SearchOutlined, {}), size: 'small', style: { width: 90 }, children: "T\u00ECm" }), _jsx(Button, { onClick: () => {
                                clearFilters && clearFilters();
                                confirm({ closeDropdown: false });
                            }, size: 'small', style: { width: 90 }, children: "X\u00F3a" }), _jsx(Button, { type: 'link', size: 'small', onClick: () => {
                                confirm({ closeDropdown: false });
                            }, children: "L\u1ECDc" }), _jsx(Button, { type: 'link', size: 'small', onClick: () => {
                                close();
                            }, children: "\u0110\u00F3ng" })] })] })),
        filterIcon: (filtered) => (_jsx(SearchOutlined, { style: { fontSize: '14px', color: filtered ? '#1677ff' : undefined } })),
        onFilter: (value, record) => {
            const searchText = value.toLowerCase();
            // Try to get value from form first (for form-based data)
            let fieldValue;
            if (childrenKey) {
                // For tree data, get from record
                fieldValue = record?.[dataIndex];
            }
            else {
                // For flat data, try to get from form
                const recordIndex = record?.name; // Form.List field index
                if (recordIndex !== undefined) {
                    fieldValue = form.getFieldValue([name, recordIndex, dataIndex]);
                }
                // Fallback to record value if form value not found
                if (fieldValue === undefined) {
                    fieldValue = record?.[dataIndex];
                }
            }
            // Handle different data structures
            let searchableText = '';
            if (typeof fieldValue === 'string' || typeof fieldValue === 'number') {
                searchableText = String(fieldValue);
            }
            else if (fieldValue && typeof fieldValue === 'object') {
                // For objects, search in name, code, text, or other common properties
                searchableText = `${fieldValue.name || ''} ${fieldValue.code || ''} ${fieldValue.text || ''} ${fieldValue.label || ''}`.trim();
            }
            return searchableText.toLowerCase().includes(searchText);
        },
        onFilterDropdownOpenChange(visible) {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        }
    });
    return (_jsx("div", { className: `${styles.editableTableWrapper} ${seamlessMode ? styles.seamlessMode : ''}`, children: _jsx(Form.List, { name: name, initialValue: [], children: (fields, operation) => {
                // build dataSource using mapTree(rawData) so keys match genKey logic
                const dataSource = childrenKey ? buildDataSourceWithKeys() : fields;
                return (_jsx(_Fragment, { children: _jsx(Table, { ...props, title: () => (_jsxs(Flex, { justify: 'space-between', align: 'center', wrap: 'wrap', children: [_jsx("span", { children: typeof props?.title === 'function' ? props.title(dataSource) : props?.title }), _jsxs(Space, { children: [childrenKey && dataSource.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Button, { type: 'link', icon: _jsx(ExpandOutlined, {}), onClick: () => setExpandedRowKeys(getAllKeys(dataSource)), children: "X\u1ED5 t\u1EA5t c\u1EA3" }), _jsx(Button, { type: 'link', icon: _jsx(CompressOutlined, {}), onClick: () => setExpandedRowKeys([]), children: "\u0110\u00F3ng t\u1EA5t c\u1EA3" })] })), props.titleActions?.map((a) => a), !readOnly && canAdd && (_jsx("div", { className: styles.addButtonWrapper, children: _jsx(CGButton, { variant: 'outlined', color: '#1677ff', icon: _jsx(PlusOutlined, {}), size: addBtnSize, onClick: () => {
                                                    if (onAdd)
                                                        onAdd(operation.add);
                                                    else
                                                        operation.add(typeof initialValue === 'function' ? initialValue() : initialValue, fields.length);
                                                }, children: addText }) }))] })] })), indentSize: 20, className: `${styles.editableTable} ${childrenKey || 'tree-table'}`, onRow: (record, index) => ({
                            onClick: () => handleRowClick(record, index || 0),
                            style: { cursor: canEdit && !readOnly ? 'pointer' : 'default' },
                            'data-editing': isEditing(index || 0)
                        }), expandable: childrenKey
                            ? {
                                expandedRowKeys,
                                onExpandedRowsChange: (keys) => setExpandedRowKeys(keys)
                            }
                            : undefined, columns: [
                            ...columns.map((col) => {
                                // Check if column should have search functionality
                                const colKey = String(col.key ?? col.dataIndex ?? '');
                                const shouldHaveSearch = searchableColumns?.includes(colKey);
                                // Apply search props if needed
                                const enhancedCol = shouldHaveSearch ? { ...col, ...getColumnSearchProps(colKey) } : col;
                                return {
                                    ...enhancedCol,
                                    render: (value, record, index) => {
                                        const originalRender = col.render ? (col.render(value, record, index, operation)) : (_jsx("div", { className: isEditing(index) ? styles.editingCell : styles.staticCell, children: value }));
                                        if (copyableColumns?.includes(colKey)) {
                                            // Get the actual value from form for copy functionality
                                            const actualValue = childrenKey ? record[colKey] : form.getFieldValue([name, index, colKey]);
                                            return (_jsx(Dropdown, { menu: { items: contextItems(colKey, actualValue, record, index) }, trigger: ['contextMenu'], children: _jsx("div", { style: { cursor: 'alias' }, children: originalRender }) }));
                                        }
                                        return originalRender;
                                    }
                                };
                            }),
                            ...(!readOnly && (actions || canDelete)
                                ? [
                                    {
                                        title: '...',
                                        key: 'action',
                                        align: 'center',
                                        fixed: 'right',
                                        width: actionWidth ?? `${(actions?.length || 0) * 40 + (canDelete ? 50 : 0)}px`,
                                        render: (_, record, index) => (_jsxs(Space, { size: 'middle', children: [actions && actions(record, index), canDelete && (_jsx(Button, { type: 'text', size: 'small', title: 'X\u00F3a d\u00F2ng', danger: true, icon: _jsx(DeleteOutlined, {}), disabled: disableDelete && disableDelete(record, index), onClick: async (e) => {
                                                        e.stopPropagation();
                                                        if (onRemoved)
                                                            await onRemoved(record, index);
                                                        if (!childrenKey) {
                                                            operation.remove(record.name);
                                                        }
                                                    }, className: styles.actionBtn }))] }))
                                    }
                                ]
                                : [])
                        ], dataSource: dataSource, pagination: pagination, bordered: bordered, size: size, 
                        // make rowKey consistent with genKey
                        rowKey: (record) => record.id ?? record.key }) }));
            } }) }));
};
